import { Category } from '@wix/answers-api/dist/kb';
import { Box, Pagination } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { FunctionComponent } from 'react';
import { animateScroll } from 'react-scroll';

import { GEN_ANSWER_RELATED_ARTICLES_COUNT } from '../../constants';
import { DATA_HOOKS } from '../../dataHooks';
import { SEARCH_RESULTS_PAGE_SIZE } from '../../pages/search';
import { ArticleSearchResult } from '../../types';
import { Breadcrumbs } from '../Breadcrumbs';
import { SkeletonLoading } from './SkeletonLoading';
import { ContactSupport } from '../ContactSupport';
import { SearchResultsHeader } from './SearchResultsHeader';
import { SearchResultItems } from './SearchResultsItems';
import { useIsGenAnswer } from '../../hooks/useIsGenAnswer';
const GeneratedAnswer = dynamic(() => import('./GeneratedAnswer'), {
  ssr: false,
});

import css from './index.module.scss';

export type SearchResultsProps = {
  term: string;
  searchResults: ArticleSearchResult;
  categoriesPaths: Record<string, Category[]>;
  currentPage: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  generatedAnswer: string | null;
  relatedArticlesTitles?: string[];
  isProhibited: boolean | null;
  showContactSupport?: boolean;
  retryGenerateAnswer: () => Promise<void>;
};

export const SearchResults: FunctionComponent<SearchResultsProps> = ({
  term,
  searchResults,
  categoriesPaths,
  currentPage,
  onPageChange,
  isLoading,
  generatedAnswer,
  relatedArticlesTitles,
  isProhibited,
  showContactSupport,
  retryGenerateAnswer,
}) => {
  const { t } = useTranslation();
  const { itemsCount, searchMethod } = searchResults;

  const isNextGenSearchEnabled = useIsGenAnswer();
  const displayGeneratedAnswer =
    isNextGenSearchEnabled && currentPage === 1 && !showContactSupport;
  const displayGeneratedAnswerLoading =
    isNextGenSearchEnabled &&
    generatedAnswer === null &&
    currentPage === 1 &&
    !showContactSupport;

  const onPageChangeEvent = async (page: number) => {
    animateScroll.scrollToTop();
    onPageChange(page);
  };

  const numPages = Math.ceil(itemsCount / SEARCH_RESULTS_PAGE_SIZE);

  const breadcrumbItems = [
    { id: 'search-results', label: t('search-results-page.breadcrumb') },
  ];
  const topArticles = searchResults.items.slice(
    0,
    GEN_ANSWER_RELATED_ARTICLES_COUNT
  );
  const relatedArticles = topArticles.filter((article) =>
    relatedArticlesTitles?.includes(article.title)
  );

  return (
    <Box
      direction="vertical"
      className={css.searchResultsWrapper}
      dataHook={DATA_HOOKS.SEARCH_RESULTS}
    >
      <Breadcrumbs breadcrumbItems={breadcrumbItems} shallow={false} />
      {showContactSupport && (
        <ContactSupport searchMethod={searchMethod as string} />
      )}
      {displayGeneratedAnswer && (
        <GeneratedAnswer
          searchQuestion={term}
          searchAnswer={generatedAnswer}
          articleLinks={relatedArticles}
          isProhibited={isProhibited}
          retryGenerateAnswer={retryGenerateAnswer}
        />
      )}
      <Box
        direction="vertical"
        className={classNames(css.searchResults, {
          [css.includeGeneratedAnswer]: displayGeneratedAnswer,
        })}
      >
        <SearchResultsHeader
          term={term}
          searchResults={searchResults}
          displayGeneratedAnswer={displayGeneratedAnswer}
          displayGeneratedAnswerLoading={displayGeneratedAnswerLoading}
        />
        {isLoading ? (
          <SkeletonLoading />
        ) : (
          <SearchResultItems
            searchResults={searchResults}
            categoriesPaths={categoriesPaths}
            term={term}
          />
        )}
      </Box>
      {!(isLoading || displayGeneratedAnswerLoading) &&
        itemsCount > SEARCH_RESULTS_PAGE_SIZE && (
          <Pagination
            className={css.pagination}
            onChange={(e) => onPageChangeEvent(e.page)}
            currentPage={currentPage}
            totalPages={numPages}
            dataHook={DATA_HOOKS.SEARCH_RESULTS_PAGINATION}
          />
        )}
    </Box>
  );
};
